import React, { useMemo, useRef } from 'react'
import TextButton from 'components/Luxkit/Button/TextButton'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import Spacing from 'components/Common/Spacing'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import clsx from 'clsx'
import StepCheckedCircle from './StepCheckedCircle'

const CheckoutStepsWrapper = styled.div`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const StepsContentItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} 0;
`

const StepActiveCircle = styled.div`
  width: ${rem(18)};
  height: ${rem(18)};
  border: 3px solid ${({ theme }) => theme.palette.brand.primary.normal};
  border-radius: 100%;
`

const StepLinePendingCircle = styled.div`
  width: ${rem(18)};
  height: ${rem(18)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.six};
  border-radius: 100%;
`

const StepVerticalLine = styled.div`
  width: ${rem(1)};
  height: ${rem(24)};
  background: ${({ theme }) => theme.palette.neutral.default.four};
  margin-left: ${rem(12)};

  &.completed {
    background: ${({ theme }) => theme.palette.neutral.default.one};
  }
`

interface CheckoutStep {
  label: string;
  step: string;
  isRoute: boolean;
  subSteps?: CheckoutStep[];
}

interface Props {
  currentStepIndex: number;
  steps: CheckoutStep[];
}

function CheckoutMobileSteps({ currentStepIndex, steps }: Props) {
  const { off, value, toggle } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const currentStepName = useMemo(() => steps[currentStepIndex]?.label, [currentStepIndex, steps])

  return <CheckoutStepsWrapper>
    <TextButton
      ref={triggerRef}
      kind="tertiary"
      endIcon={<LineAngleDownIcon />}
      onClick={toggle}
    >
      <BodyText
        variant="medium"
        weight="bold"
      >{currentStepName}</BodyText>
    </TextButton>

    <Dropdown open={value} onClose={off} triggerRef={triggerRef}>
      <DropdownContainer>
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex
          const isActive = index === currentStepIndex
          const isPending = index > currentStepIndex
          const isLast = index === steps.length - 1

          return <div key={step.step}>
            <StepsContentItem>
              <Spacing ml={4}>
                {isCompleted && <StepCheckedCircle />}
                {isActive && <StepActiveCircle />}
                {isPending && <StepLinePendingCircle />}
              </Spacing>

              <BodyText
                variant="medium"
                weight={isActive ? 'bold' : 'normal'}
                colour={isPending ? 'neutral-four' : undefined}
              >{index + 1}. {step.label}</BodyText>
            </StepsContentItem>

            {!isLast && <StepVerticalLine className={clsx({ completed: isCompleted })} />}
          </div>
        })}
      </DropdownContainer>
    </Dropdown>
  </CheckoutStepsWrapper>
}

export default CheckoutMobileSteps
